import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Autocomplete, Box, Button, Chip, TextField, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'

interface Lead {
  lead_uuid: string
  name: string
  company: string
  email?: string
  phone?: string
  status?: string
  quality?: string
  submittedDate?: string
}

interface LeadSelectorFormProps {
  title: string
  leads: Lead[]
  onSubmit: (message: string) => void
  isInteractive: boolean
}

function formatDate(d?: string) {
  if (!d) return ''
  const parsed = new Date(d)
  if (isNaN(parsed.getTime())) return d
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const qualityColor: Record<string, string> = {
  Hot: '#f44336',
  Warm: '#ff9800',
  Cold: '#2196f3',
}

const LeadSelectorForm: React.FC<LeadSelectorFormProps> = ({
  title,
  leads,
  onSubmit,
  isInteractive,
}) => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const unconvertedLeads = useMemo(
    () => leads.filter(l => l.status !== 'Converted').sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [leads]
  )

  const handleSubmit = () => {
    if (!selectedLead) return
    const message = `Convert lead "${selectedLead.name}" from ${selectedLead.company} (lead_uuid: ${selectedLead.lead_uuid})`
    setSubmitted(true)
    onSubmit(message)
  }

  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 400,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted && selectedLead
            ? `Selected: ${selectedLead.name} — ${selectedLead.company}`
            : title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        maxWidth: 400,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
        {title}
      </Typography>

      <Autocomplete
        size="small"
        options={unconvertedLeads}
        value={selectedLead}
        onChange={(_, value) => setSelectedLead(value)}
        getOptionLabel={(option: Lead) => `${option.name} — ${option.company}`}
        isOptionEqualToValue={(option, value) => option.lead_uuid === value.lead_uuid}
        renderOption={(props, option) => {
          const { key, ...rest } = props as any
          return (
            <Box
              component="li"
              key={option.lead_uuid}
              {...rest}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start !important',
                gap: 0.25,
                py: 0.75,
                px: 1.5,
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.3 }}>
                {option.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary' }}>
                  {option.company}
                </Typography>
                {option.quality && (
                  <Chip
                    label={option.quality}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      bgcolor: qualityColor[option.quality] || 'grey.500',
                      color: '#fff',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                )}
                {option.status && (
                  <Chip
                    label={option.status}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                )}
              </Box>
              {option.email && (
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                  {option.email}
                  {option.submittedDate ? ` — ${formatDate(option.submittedDate)}` : ''}
                </Typography>
              )}
            </Box>
          )
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder="Search leads..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                fontSize: '0.8rem',
                bgcolor: 'background.default',
              },
            }}
          />
        )}
        sx={{ width: '100%' }}
      />

      <Button
        size="small"
        variant="outlined"
        onClick={handleSubmit}
        disabled={!selectedLead}
        sx={{
          alignSelf: 'flex-end',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderColor: 'text.ai_agent_text',
          color: 'text.ai_agent_text',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            borderColor: 'text.highlight',
            color: 'text.primary',
          },
        }}
      >
        Select Lead
      </Button>
    </Box>
  )
}

export default LeadSelectorForm
